import React, { useContext, useMemo, useEffect, forwardRef } from 'react';
import classNames from 'classnames';
import NotificationsContext from 'providers/Notifications/Notifications.context';
import InfiniteScroll from 'components/InfiniteScroll';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import NotificationsHeader from 'components/NotificationsHeader';
import useNotifications from 'router/subrouters/Dash/subrouters/hooks/useNotifications';
import LeadNotificationsContext from 'providers/LeadNotifications/LeadNotifications.context';
import useLeadNotifications from 'router/subrouters/Dash/subrouters/hooks/useLeadNotifications';
import { getIsNotificationForLeads } from 'utils';
import NotificationsForLeadsFilters from 'components/NotificationsForLeadsFilters';
import useScrollToStart from 'hooks/useScrollToStart';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';

import './NotificationsDrawer.styles.scss';
import './NotificationsDrawer.styles.responsive.scss';

type NotificationsDrawerProps = {
  className?: string;
};

const NotificationsDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  NotificationsDrawerProps
> = (props, ref) => {
  const { className } = props;

  const {
    isDrawerOpen,
    notificationBy,
    notificationGroup,
    notificationStatus,
    closeDrawer,
    onNotificationsScroll,
  } = useContext(NotificationsContext);
  const {
    onNotificationsScroll: onLeadNotificationsScroll,
    channel,
    status,
    leads,
    deals,
  } = useContext(LeadNotificationsContext);

  const { showEmptyState, placeholders, renderedCards } = useNotifications();

  const {
    showEmptyState: leadShowEmptyState,
    placeholders: leadPlaceholders,
    renderedCards: leadRenderedCards,
  } = useLeadNotifications();

  const isForLeads = getIsNotificationForLeads(notificationBy);

  const [scrollRef, scrollToStart] = useScrollToStart();

  useEffect(() => {
    if (!isDrawerOpen) return;

    scrollToStart();
  }, [
    channel,
    status,
    leads,
    deals,
    notificationGroup,
    notificationBy,
    notificationStatus,
    isDrawerOpen,
    scrollToStart,
  ]);

  const shouldShowEmptyState = useMemo(
    () => (isForLeads && leadShowEmptyState) || (!isForLeads && showEmptyState),
    [isForLeads, leadShowEmptyState, showEmptyState],
  );

  const classes = classNames('notifications-drawer', className);

  return (
    <SideDrawer
      ref={ref}
      className="notifications-side-drawer"
      overlayClassName="notifications-side-drawer__overlay"
      closeAction={closeDrawer}
    >
      <InfiniteScroll
        localScroll
        className={classes}
        onScroll={
          isForLeads ? onLeadNotificationsScroll : onNotificationsScroll
        }
      >
        <NotificationsHeader showExpand onClose={closeDrawer} />

        <NotificationsForLeadsFilters />

        {shouldShowEmptyState ? (
          <ReportsEmptyState className="notifications-drawer__empty-state" />
        ) : (
          <div ref={scrollRef} className="notifications-drawer__list">
            {isForLeads ? leadRenderedCards : renderedCards}
            {isForLeads ? leadPlaceholders : placeholders}
          </div>
        )}
      </InfiniteScroll>
    </SideDrawer>
  );
};

export default forwardRef(NotificationsDrawer);
